import type { PlayerSettings } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { LANGUAGE_NAMES } from '../screens/SettingsScreen.js';

/** Le voci che hanno un valore da scorrere, non un interruttore. */
export type PhoneSettingChoice = 'imageQuality' | 'guideHours' | 'language';

/**
 * Le Opzioni sul telefono: **interruttori a levetta**, non righe con un pannello a lato.
 *
 * ## Perché la forma cambia
 *
 * Sul televisore ogni voce ha un pannello esplicativo accanto, perché c'è spazio e perché col
 * telecomando leggere è l'unico modo di capire cosa si sta per cambiare. Su 390 px quel pannello non
 * ci sta: l'aiuto diventa un **sottotitolo sotto la voce**, sempre visibile invece che accanto
 * (`04-SCHERMATE-MOBILE.md` §7).
 *
 * Non si accorcia: è lo stesso testo, perché spiega la stessa cosa — «risparmio risorse» da solo non
 * dice a nessuno che spegne le animazioni e abbassa le immagini.
 *
 * ## Le voci con un valore non si decidono qui
 *
 * `onCycle` chiede a chi monta la schermata di passare al valore successivo. La regola di come si
 * gira — e dove si ferma — esiste già per il televisore, e riscriverla qui sarebbe la seconda copia
 * di un giro che deve restare uno solo.
 *
 * ## Il PIN apre una schermata, non un modale
 *
 * È l'unica voce che porta altrove. Su un telefono la tastiera numerica di sistema **coprirebbe i
 * pallini** che si stanno digitando, quindi il PIN ha una schermata sua con il proprio tastierino.
 * Resta un modale solo la richiesta prima di riprodurre, come sulla TV.
 */
export function PhoneSettingsScreen({
  settings,
  onToggle,
  onCycle,
  onOpenPin,
}: {
  readonly settings: PlayerSettings;
  readonly onToggle: (key: 'resourceSaving' | 'animations' | 'visualEffects') => void;
  readonly onCycle: (key: PhoneSettingChoice) => void;
  readonly onOpenPin: () => void;
}) {
  const translator = useTranslator();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '8px 0 28px' }}>
      <h1 style={{ margin: 0, padding: '0 20px', font: `800 28px ${FONT_STACK}`, letterSpacing: '-.02em' }}>
        {translator.t('menu.settings')}
      </h1>

      <Gruppo titolo={translator.t('settings.group.performance')}>
        <Levetta
          label={translator.t('settings.resourceSaving')}
          help={translator.t('settings.resourceSaving.help')}
          on={settings.resourceSaving}
          onToggle={() => onToggle('resourceSaving')}
        />
        <Levetta
          label={translator.t('settings.animations')}
          help={translator.t('settings.animations.help')}
          on={settings.animations}
          onToggle={() => onToggle('animations')}
        />
        <Levetta
          label={translator.t('settings.visualEffects')}
          help={translator.t('settings.visualEffects.help')}
          on={settings.visualEffects}
          onToggle={() => onToggle('visualEffects')}
        />
      </Gruppo>

      <Gruppo titolo={translator.t('settings.group.content')}>
        <Valore
          label={translator.t('settings.imageQuality')}
          help={translator.t('settings.imageQuality.help')}
          value={translator.t(`settings.quality.${settings.imageQuality}`)}
          onCycle={() => onCycle('imageQuality')}
        />
        <Valore
          label={translator.t('settings.guideHours')}
          help={translator.t('settings.guideHours.help')}
          value={translator.t('settings.hours', { count: settings.guideHours })}
          onCycle={() => onCycle('guideHours')}
        />
        <Valore
          label={translator.t('settings.language')}
          help={translator.t('settings.language.help')}
          value={
            settings.locale === 'auto'
              ? translator.t('settings.language.auto')
              : (LANGUAGE_NAMES[settings.locale] ?? settings.locale)
          }
          onCycle={() => onCycle('language')}
        />
      </Gruppo>

      <Gruppo titolo={translator.t('settings.group.parental')}>
        <Valore
          label={translator.t('settings.adultPin')}
          help={translator.t('settings.adultPin.help')}
          value={translator.t(settings.adultPinEnabled ? 'settings.adultPin.on' : 'settings.adultPin.off')}
          /*
            Porta a una schermata, non cambia un valore. È l'unica voce che lo fa, e il motivo è
            fisico: la tastiera numerica di sistema coprirebbe i pallini che si stanno digitando.
          */
          onCycle={onOpenPin}
        />
      </Gruppo>
    </div>
  );
}

function Gruppo({ titolo, children }: { readonly titolo: string; readonly children: React.ReactNode }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column' }}>
      <h2
        style={{
          margin: 0,
          padding: '0 20px 8px',
          font: `700 12px ${FONT_STACK}`,
          color: colors.textDim,
          letterSpacing: '.08em',
          textTransform: 'uppercase',
        }}
      >
        {titolo}
      </h2>
      {children}
    </section>
  );
}

/**
 * Una voce con l'interruttore.
 *
 * Tutta la riga è il bersaglio, non la sola levetta: una levetta è larga una cinquantina di pixel in
 * fondo a destra, e mancarla su un telefono è la norma. `role="switch"` con `aria-checked` perché un
 * lettore di schermo deve poter dire se è accesa — il colore non glielo dice.
 */
function Levetta({
  label,
  help,
  on,
  onToggle,
}: {
  readonly label: string;
  readonly help: string;
  readonly on: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      style={{
        width: '100%',
        border: 0,
        borderBottom: `1px solid ${colors.borderFaint}`,
        background: 'none',
        color: colors.text,
        textAlign: 'left',
        cursor: 'pointer',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 14,
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ font: `600 15px ${FONT_STACK}` }}>{label}</span>
        {/*
          L'aiuto sotto la voce, per intero. Sul televisore sta in un pannello a lato; qui non ci
          starebbe, e accorciarlo toglierebbe proprio quel che spiega — «risparmio risorse» da solo
          non dice che spegne le animazioni e abbassa le immagini.
        */}
        <span style={{ font: `500 12px ${FONT_STACK}`, color: colors.textDim, lineHeight: 1.4 }}>
          {help}
        </span>
      </span>
      <span
        aria-hidden
        style={{
          flex: 'none',
          width: 46,
          height: 28,
          borderRadius: 14,
          background: on ? colors.accent : colors.border,
          display: 'flex',
          alignItems: 'center',
          padding: 3,
          boxSizing: 'border-box',
          justifyContent: on ? 'flex-end' : 'flex-start',
        }}
      >
        <span style={{ width: 22, height: 22, borderRadius: 11, background: colors.text }} />
      </span>
    </button>
  );
}

/** Una voce con un valore: toccandola si passa al successivo, o si apre dove porta. */
function Valore({
  label,
  help,
  value,
  onCycle,
}: {
  readonly label: string;
  readonly help: string;
  readonly value: string;
  readonly onCycle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onCycle}
      style={{
        width: '100%',
        border: 0,
        borderBottom: `1px solid ${colors.borderFaint}`,
        background: 'none',
        color: colors.text,
        textAlign: 'left',
        cursor: 'pointer',
        padding: '12px 20px',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 14,
      }}
    >
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ font: `600 15px ${FONT_STACK}` }}>{label}</span>
        <span style={{ font: `500 12px ${FONT_STACK}`, color: colors.textDim, lineHeight: 1.4 }}>
          {help}
        </span>
      </span>
      <span style={{ flex: 'none', font: `600 14px ${FONT_STACK}`, color: colors.accent }}>{value}</span>
    </button>
  );
}
